<template>
  <v-card class="mb-8 pa-0 elevation-2">
    <v-card-title class="text-h5 font-weight-bold pa-6 pb-4">
      <v-icon left color="primary" class="mr-3">mdi-file-document-edit</v-icon>
      Contenu Editorial
    </v-card-title>
    
    <v-divider></v-divider>
    
    <v-card-text class="pa-6">
      <!-- Top Stats Row -->
      <v-row class="mb-6">
        <v-col cols="12" md="4">
          <div class="stat-card stat-card-articles">
            <div class="stat-left">
              <div class="stat-label">Articles<br>Créés</div>
            </div>
            <div class="stat-center">
              <div class="text-h4 font-weight-bold stat-value">{{ formatArticlesCreated }}</div>
            </div>
            <div class="stat-right" v-if="articlesCreatedTrend !== null">
              <v-icon :color="articlesCreatedTrendColor">{{ articlesCreatedTrendIcon }}</v-icon>
              <span class="trend-value">{{ Math.abs(articlesCreatedTrend) }}%</span>
            </div>
          </div>
        </v-col>
      </v-row>
      
      <!-- Site Charts -->
      <v-row>
        <v-col cols="12">
          <div class="chart-card">
            <div class="chart-header">
              <v-icon color="warning" class="mr-2">mdi-trending-down</v-icon>
              <h3 class="text-h6 font-weight-bold">Baisse de création d'articles</h3>
            </div>
            
            <!-- Dynamic Site Charts -->
            <div v-if="postCreationDropSites.length > 0">
              <div 
                v-for="site in visiblePostCreationDropSites" 
                :key="site.id"
                class="site-chart-item">
                <div class="site-name">{{ site.name }}</div>
                <BarChartFixed 
                  :bars="generateBarsFromData(site.data)"
                  :tooltip-style="tooltipStyle"
                />
              </div>
              
              <!-- Expand/Collapse Button -->
              <div v-if="postCreationDropSites.length > 3" class="text-center mt-3">
                <v-btn
                  text
                  small
                  color="primary"
                  @click="expandedSections.postCreationDrops = !expandedSections.postCreationDrops"
                >
                  <v-icon left small>
                    {{ expandedSections.postCreationDrops ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  {{ expandedSections.postCreationDrops ? 'Voir moins' : `Voir ${postCreationDropSites.length - 3} de plus` }}
                </v-btn>
              </div>
            </div>
            
            <!-- Loading State -->
            <div v-else-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-3 text--secondary">Chargement des données...</div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center py-8 text--secondary">
              <v-icon large class="mb-3">mdi-check-circle</v-icon>
              <div>Aucune baisse importante détectée</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import BarChartFixed from '@/components/admin/data-availability/charts/BarChartFixed.vue';
import { formatNumberCompact } from '@/utils/formatting';
import { mdiArrowTopRight, mdiArrowBottomLeft } from '@mdi/js';

export default {
  name: 'EditorialOverviewCard',
  components: {
    BarChartFixed
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      icons: {
        mdiArrowTopRight,
        mdiArrowBottomLeft
      },
      tooltipStyle: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '8px 12px',
        fontSize: '14px',
        color: '#333',
        whiteSpace: 'nowrap'
      },
      expandedSections: {
        postCreationDrops: false
      }
    };
  },
  methods: {
    generateBarsFromData(siteData) {
      // Convert API data to bar chart format
      if (!siteData || !Array.isArray(siteData)) {
        return [];
      }
      
      const bars = siteData.map((dataPoint, index) => {
        const isLastBar = index === siteData.length - 1;
        const hasIssue = dataPoint.hasIssue || dataPoint.value === 0;
        
        const value = dataPoint.value || dataPoint.postsCount || 0;
        
        const bar = {
          height: this.normalizePostCountHeight(value),
          tooltip: this.formatDataTooltip(dataPoint),
          blinking: isLastBar && hasIssue,
          color: hasIssue ? '#ff2000' : '#56ca00'
        };
        
        return bar;
      });
      
      return bars;
    },
    normalizePostCountHeight(value) {
      // Handle zero values
      if (value === 0) {
        return 5; // Minimum visible height for zero values
      }
      
      // For post counts, use a logarithmic scale for better visual distribution
      const logValue = Math.log10(value + 1);
      
      // Typical post counts might range from 0 to ~500
      // log10(1) = 0, log10(10) ≈ 1, log10(100) = 2, log10(500) ≈ 2.7
      const maxExpectedLog = Math.log10(501); // ≈ 2.7
      
      // Map the log value to height percentage
      const minHeight = 15;
      const maxHeight = 85;
      
      // Calculate normalized height
      const normalizedLog = Math.min(logValue / maxExpectedLog, 1); // Cap at 1
      const height = minHeight + (normalizedLog * (maxHeight - minHeight));
      
      return Math.round(height);
    },
    formatDataTooltip(dataPoint) {
      const date = dataPoint.date ? new Date(dataPoint.date).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit' 
      }) : 'N/A';
      
      const postsCount = dataPoint.postsCount || dataPoint.value || 0;
      return `${date} - ${postsCount} article${postsCount > 1 ? 's' : ''}`;
    }
  },
  computed: {
    postCreationDropSites() {
      // Get sites with post creation drop issues from API response
      if (this.data && this.data.editorialIssues && this.data.editorialIssues.postCreationDrop) {
        return this.data.editorialIssues.postCreationDrop;
      }
      return [];
    },
    visiblePostCreationDropSites() {
      // Show only first 3 sites unless expanded
      if (this.expandedSections.postCreationDrops || this.postCreationDropSites.length <= 3) {
        return this.postCreationDropSites;
      }
      return this.postCreationDropSites.slice(0, 3);
    },
    // Header stats computed properties
    formatArticlesCreated() {
      const value = this.data?.stats?.articlesCreated?.value || 0;
      return formatNumberCompact(value, 0);
    },
    articlesCreatedTrend() {
      return this.data?.stats?.articlesCreated?.trend || null;
    },
    articlesCreatedTrendColor() {
      if (this.articlesCreatedTrend === null) return 'grey';
      return this.articlesCreatedTrend >= 0 ? 'success' : 'error';
    },
    articlesCreatedTrendIcon() {
      if (this.articlesCreatedTrend === null) return '';
      const direction = this.data?.stats?.articlesCreated?.trendDirection || (this.articlesCreatedTrend >= 0 ? 'up' : 'down');
      return direction === 'up' ? this.icons.mdiArrowTopRight : this.icons.mdiArrowBottomLeft;
    }
  }
};
</script>

<style scoped>
/* Stat cards with modern styling */
.stat-card {
  background-color: #f4f5fa;
  padding: 20px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e0e6ee;
  box-shadow: 0 0 0px #e0e0e0;
  min-height: 100px;
  cursor: pointer;
}

.stat-card:hover {
  background-color: #f4f6ff;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.stat-card:active {
  background-color: #f4f5fa;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
}

.stat-left {
  flex-shrink: 0;
  min-width: 80px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  line-height: 1.3;
}

.stat-center {
  flex: 1;
  text-align: center;
  padding: 0 16px;
}

.stat-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-value {
  color: #1a1a1a;
  margin-bottom: 4px;
}

.trend-value {
  font-size: 14px;
  font-weight: 600;
  color: #2e7d32;
}

/* Chart cards styling */
.chart-card {
  border: 1px solid #e0e6ee;
  background-color: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 0 0px #e0e0e0;
  height: 100%;
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

/* Site chart items */
.site-chart-item {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f4f5fa;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #e0e6ee;
  box-shadow: 0 0 0px #e0e0e0;
}

.site-chart-item:hover {
  background-color: #f4f6ff;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.site-chart-item:last-child {
  margin-bottom: 0;
}

.chart-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.site-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #565656;
  margin-bottom: 12px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .stat-card {
    margin-bottom: 16px;
  }
  
  .chart-card {
    margin-bottom: 16px;
  }
}
</style>